import styled, { css } from 'styled-components';

import { rem } from 'approot/shared/style';
import {
  spacing12,
  spacing16,
  spacing24,
  spacing4,
  spacing8,
} from 'approot/shared/styles/spacing.style';
import { borderRadius8 } from 'approot/shared/styles/borders.style';
import { grey1, grey4 } from 'approot/shared/styles/colours.style';
import { useActivityBuilderFontStyles } from 'approot/activity-builder/theme/activity-builder-theme.style';
import {
  FONT_CALIBRE_SCHOOL,
  fontSize14,
  fontWeightMedium,
} from 'approot/shared/styles/typography.style';
import { ColourItem } from 'approot/shared/colour/colour-token.style';
import {
  ACTIVITIES_OPTION_SIZE_HUGE,
  ACTIVITIES_OPTION_SIZE_LARGE,
  ACTIVITIES_OPTION_SIZE_MEDIUM,
} from 'global/constants';
import {
  FontFamily,
  FontSize,
  useFillInBlankFontSizes,
} from './use-fill-in-blanks-font-sizes';

function getFieldMarginValues(fontSize: FontSize | null, hasLabel: boolean) {
  switch (fontSize) {
    case ACTIVITIES_OPTION_SIZE_HUGE:
      return `${rem(hasLabel ? 22 : spacing12)} ${rem(24)} 0 ${rem(24)}`;
    case ACTIVITIES_OPTION_SIZE_LARGE:
      return `${rem(hasLabel ? 20 : spacing8)} ${rem(16)} 0 ${rem(16)}`;
    case ACTIVITIES_OPTION_SIZE_MEDIUM:
      return `${rem(hasLabel ? 18 : spacing4)} ${rem(12)} 0 ${rem(12)}`;
    default:
      return `${rem(hasLabel ? 18 : spacing4)} ${rem(6)} 0 ${rem(6)}`;
  }
}

function getQuestionMarkPunctuationSpacing(fontSize: FontSize | null) {
  switch (fontSize) {
    case ACTIVITIES_OPTION_SIZE_HUGE:
      return rem(34);
    case ACTIVITIES_OPTION_SIZE_LARGE:
      return rem(25);
    case ACTIVITIES_OPTION_SIZE_MEDIUM:
      return rem(18);
    default:
      return rem(spacing16);
  }
}

function getQuotesMarkPunctuationSpacing(fontSize: FontSize | null) {
  switch (fontSize) {
    case ACTIVITIES_OPTION_SIZE_HUGE:
      return rem(26);
    case ACTIVITIES_OPTION_SIZE_LARGE:
      return rem(20);
    case ACTIVITIES_OPTION_SIZE_MEDIUM:
      return rem(14);
    default:
      return rem(12);
  }
}

function getFullStopPunctuationSpacing(fontSize: FontSize | null) {
  switch (fontSize) {
    case ACTIVITIES_OPTION_SIZE_HUGE:
      return rem(16);
    case ACTIVITIES_OPTION_SIZE_LARGE:
      return rem(12);
    case ACTIVITIES_OPTION_SIZE_MEDIUM:
      return rem(10);
    default:
      return rem(8);
  }
}

export type FillInBlanksFontSizeProps = {
  $fontSize: FontSize | null;
  $fontFamily: FontFamily;
};
export const FillInBlanksFontSizeCss = css<FillInBlanksFontSizeProps>`
  ${({ $fontSize, $fontFamily }) =>
    useFillInBlankFontSizes({
      fontFamily: $fontFamily,
      size: $fontSize,
    })}
`;

export const FillInBlanksFieldCss = css<
  FillInBlanksFontSizeProps & {
    $hasLabel: boolean;
    $colours: ColourItem;
  }
>`
  ${({ $colours }) =>
    $colours &&
    `
    border-bottom: 1px solid ${$colours.base};
    background-color: ${$colours.colour1};
  `}

  margin: ${({ $fontSize, $hasLabel }) =>
    getFieldMarginValues($fontSize, $hasLabel)};

  position: relative;
`;

type FillInBlanksPunctuationProps = {
  $punctuation: string | null;
} & FillInBlanksFontSizeProps;
export const FillInBlanksPunctuationCss = css<FillInBlanksPunctuationProps>`
  ${({ $punctuation, $fontSize }) =>
    $punctuation &&
    $punctuation === '?' &&
    `
      margin-right: ${getQuestionMarkPunctuationSpacing($fontSize)};

      &:before {
        content: '${$punctuation}';
        position: absolute;
        right: -${getQuestionMarkPunctuationSpacing($fontSize)};
      }
    `};

  ${({ $punctuation, $fontSize }) =>
    $punctuation &&
    ($punctuation === '!' || $punctuation === ':' || $punctuation === ';') &&
    `
    &:before {
      content: '${$punctuation}';
      position: absolute;
      right: -${getFullStopPunctuationSpacing($fontSize)};
    }
  `};

  ${({ $punctuation, $fontSize }) =>
    $punctuation &&
    ($punctuation === '.' || $punctuation === ',') &&
    `
    &:before {
      content: '${$punctuation}';
      position: absolute;
      right: -${getFullStopPunctuationSpacing($fontSize)};
      bottom: -${rem(spacing8)};
    }
  `};

  ${({ $punctuation, $fontSize }) =>
    $punctuation === '"' &&
    `
      margin-left: ${rem(spacing12)};

      &:before {
        content: '“';
        position: absolute;
        left: -${getQuotesMarkPunctuationSpacing($fontSize)};
        top: -${rem(spacing8)};
      }
      &:after {
        content: '”';
        position: absolute;
        right: -${getQuotesMarkPunctuationSpacing($fontSize)};
        top: -${rem(spacing8)};
      }
    `};
`;

export const FillInBlanksTextBlock = styled.div`
  ${() => useActivityBuilderFontStyles()}
  margin: 0 ${rem(spacing8)};
  display: inline;
  p,
  span {
    display: inline;
  }
`;

type FillInBlanksLabelSupportProps = {
  $label: string | null;
  $colours: ColourItem;
};
export const FillInBlanksLabelSupport = css<FillInBlanksLabelSupportProps>`
  ${({ $label, $colours }) =>
    $label &&
    `
    &:after {
      content: '${$label}';
      color: ${$colours.base};
      display: inline-block;
      font-family: ${FONT_CALIBRE_SCHOOL};
      font-size: ${rem(fontSize14)};
      font-weight: ${fontWeightMedium};
      position: absolute;
      left: 0;
      top: -${rem(spacing16)};
      line-height: ${rem(spacing16)};
      z-index: 2;
      white-space: nowrap;
    }
  `};
`;

type FillInBlanksSentencesWrapperProps = {
  $fontFamily: FontFamily;
  $fontSize: FontSize | null;
};
export const FillInBlanksSentencesWrapper = styled.div<FillInBlanksSentencesWrapperProps>`
  background-color: ${grey1};
  border-radius: ${rem(borderRadius8)};
  border: 1px solid ${grey4};
  padding: ${rem(spacing12)} 0;
  width: 100%;

  ${({ $fontFamily }) =>
    $fontFamily &&
    `
      font-family: ${$fontFamily};
      p {
        font-family: ${$fontFamily};
      }
    `}

  ${FillInBlanksTextBlock} {
    ${FillInBlanksFontSizeCss}
  }
`;

export const FillInBlanksSentence = styled.div`
  padding: ${rem(spacing4)} ${rem(spacing24)};
  position: relative;

  & > div:first-child {
    margin-left: 0;
  }
`;
